import { TransactionResponse } from "@0xsquid/sdk/dist/types";
import type { RouteResponse } from "@0xsquid/squid-types";
import { ChainType, SquidDataType } from "@0xsquid/squid-types";
import { SigningCosmWasmClient } from "@cosmjs/cosmwasm-stargate";
import { SigningStargateClient } from "@cosmjs/stargate";
import { InjectiveSigningStargateClient } from "@injectivelabs/sdk-ts/dist/cjs/core/stargate/index.js";
import { Transaction as SuiTransaction } from "@mysten/sui/transactions";
import {
  BASE_FEE,
  Transaction as StellarTransaction,
  TransactionBuilder,
  xdr,
} from "@stellar/stellar-sdk";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { TxRaw } from "cosmjs-types/cosmos/tx/v1beta1/tx";
import { TransactionReceipt, isError } from "ethers";
import { useCallback, useMemo } from "react";
import { useAccount } from "wagmi";
import { getClient } from "../../core/client";
import { CHAIN_IDS } from "../../core/constants";
import { QueryKeys, getPrefixKey } from "../../core/queries/queries-keys";
import { TransactionErrorType } from "../../core/types/error";
import type { TransactionParams } from "../../core/types/transaction";
import { TransactionStatus } from "../../core/types/transaction";
import { XrplTxStatus } from "../../core/types/xrpl";
import {
  executeSolanaSwap,
  executeSolanaTransfer,
  formatBNToReadable,
  getCosmosSigningClient,
  getTransactionError,
  getXrplNetwork,
  handleTransactionErrorEvents,
  isProblematicConnector,
  isUserRejectionError,
  normalizeError,
  parseXrplPaymentTx,
  waitForReceiptWithRetry,
} from "../../services";
import { getWorkingCosmosRpcUrl } from "../../services/external/rpcService";
import { buildCantonTransfer } from "../../services/internal/cantonTransferService";
import { WidgetEvents } from "../../services/internal/eventService";
import { getStellarNetwork } from "../../services/internal/stellarService";
import { getSuiChain } from "../../services/internal/suiService";
import {
  getSourceExplorerTxUrl,
  isActionCompletedOnSourceTx,
  isChainflipDepositRoute,
  isDepositAddressDirectTransferRoute,
  isOnChainTxData,
  sleep,
} from "../../services/internal/transactionService";
import { useSquidChains } from "../chains/useSquidChains";
import { useSolanaConnection } from "../solana/useSolana";
import { useDepositAddressStore } from "../store/useDepositAddressStore";
import { useSquidStore, useTransactionStore } from "../store/useSquidStore";
import { useSwap } from "../swap/useSwap";
import { useSquidTokens } from "../tokens/useSquidTokens";
import { useHistory } from "../user/useHistory";
import { useGnosisContext } from "../wallet/useGnosisContext";
import { useMultiChainWallet } from "../wallet/useMultiChainWallet";
import { useSigner } from "../wallet/useSigner";
import { useWallet } from "../wallet/useWallet";

type SwapMutationParams = {
  route: RouteResponse["route"] | undefined;
  id: string;
};

export const useExecuteTransaction = (squidRoute?: RouteResponse["route"]) => {
  const { fromChain, toChain, fromToken, toToken, isSameChain } = useSwap();

  // A route completes on source tx only if it's same-chain AND
  // every action is fulfilled in the user's source transaction
  const isCompletedOnSourceTx =
    isSameChain &&
    !!fromChain &&
    !!squidRoute &&
    squidRoute.estimate.actions.every((a) =>
      isActionCompletedOnSourceTx(a, fromChain.chainId)
    );

  const {
    evmSigner,
    cosmosSigner,
    solanaSigner,
    bitcoinSigner,
    suiSigner,
    xrplSigner,
    stellarSigner,
    cantonSigner,
  } = useSigner({
    chain: fromChain,
  });
  const { findToken } = useSquidTokens();

  const solanaConnection = useSolanaConnection();
  const { findChain } = useSquidChains();
  const queryClient = useQueryClient();
  const { connectedWalletsByChainType } = useWallet();

  const squid = useSquidStore((state) => state.squid);
  const getTransaction = useTransactionStore((state) => state.getTransaction);
  const setTransactionStoreState = useTransactionStore(
    (state) => state.setTransactionState
  );

  const { connector: activeConnector } = useAccount();

  const { getGnosisTransactionHash } = useGnosisContext();
  const {
    addSwapTransaction,
    replaceSwapTransactionNonce,
    replaceSwapTransactionStatus,
  } = useHistory();

  const {
    connectedAddress: { address: sourceUserAddress },
    changeNetworkIfNeeded,
  } = useMultiChainWallet(fromChain);

  const connectedSourceWallet = useMemo(() => {
    return fromChain?.chainType
      ? connectedWalletsByChainType[fromChain.chainType]
      : undefined;
  }, [fromChain?.chainType, connectedWalletsByChainType]);

  /**
   * Set the transaction state in the store
   * This is useful to access the latest transaction from any hook
   */
  const setTransactionState = useCallback(
    ({
      route,
      txHash,
      transactionIdForStatus,
      nonce,
      status,
      sourceStatus,
      userAddress,
      axelarUrl,
      id,
      depositTxVerificationSignature,
    }: {
      route: RouteResponse["route"];
      txHash: string;
      transactionIdForStatus?: string;
      nonce?: number;
      status: TransactionStatus;
      sourceStatus: TransactionStatus;
      userAddress?: string;
      axelarUrl?: string;
      id: string;
      depositTxVerificationSignature?: string;
    }): TransactionParams | undefined => {
      if (!route?.transactionRequest) return undefined;

      const { quoteId } = route;

      const tx: TransactionParams = {
        fromChain,
        toChain,
        routeType: route.transactionRequest.type,
        nonce,
        transactionId: txHash,
        transactionIdForStatus,
        quoteId,
        status,
        sourceStatus,
        timestamp: Date.now(),
        fromAddress: userAddress,
        sourceTxExplorerUrl: getSourceExplorerTxUrl(fromChain, txHash),
        sourceExplorerImgUrl: fromChain?.chainIconURI,
        axelarUrl,
        depositTxVerificationSignature,
      };

      setTransactionStoreState(id, tx);
      return tx;
    },
    [fromChain, toChain, setTransactionStoreState]
  );

  const getCosmosSignerClient = useCallback(async (): Promise<
    | InjectiveSigningStargateClient
    | SigningCosmWasmClient
    | SigningStargateClient
    | null
  > => {
    if (!fromChain) return null;
    const rpc = await getWorkingCosmosRpcUrl(fromChain);

    if (!cosmosSigner) return null;

    switch (fromChain.chainId) {
      case CHAIN_IDS.INJECTIVE:
        return InjectiveSigningStargateClient.connectWithSigner(
          rpc,
          cosmosSigner
        );

      default: {
        const cosmosClient = await getCosmosSigningClient({
          chainRpc: rpc,
          cosmosSigner,
          defaultSigningClient: SigningCosmWasmClient,
        });

        return cosmosClient;
      }
    }
  }, [cosmosSigner, fromChain]);

  const resetQueriesAfterTxSigned = () => {
    queryClient.refetchQueries(getPrefixKey(QueryKeys.Balance));
    queryClient.invalidateQueries(getPrefixKey(QueryKeys.Transaction));
  };

  const dispatchSignatureRequestEvent = useCallback(
    (route: RouteResponse["route"]) => {
      WidgetEvents.getInstance().dispatchSwapTxSignatureRequested({
        route,
      });
    },
    []
  );

  const dispatchPreSwapEvent = useCallback(
    (route: RouteResponse["route"]) => {
      WidgetEvents.getInstance().dispatchPreSwap(
        {
          ...route.params,
        },
        findToken
      );
    },
    [findToken]
  );

  const dispatchPostSwapEvent = useCallback(
    (route: RouteResponse["route"], txHash: string) => {
      WidgetEvents.getInstance().dispatchPostSwap(
        {
          ...route.params,
          txHash,
        },
        findToken
      );
    },
    [findToken]
  );

  const swapMutationCosmos = useMutation(
    async ({ id, route }: SwapMutationParams) => {
      const fromChainId = route?.params.fromChain;

      if (cosmosSigner && fromChainId) {
        try {
          const signingClient = await getCosmosSignerClient();
          const signerAddress = (await cosmosSigner.getAccounts())[0].address;

          if (signerAddress && signingClient && route) {
            dispatchSignatureRequestEvent(route);

            const tx = (await squid?.executeRoute({
              signer: signingClient as any,
              signerAddress,
              route,
            })) as TxRaw;

            // set the tx state to loading, as soon as user signed the tx
            setTransactionState({
              txHash: "", // We don't have the hash yet
              route,
              status: TransactionStatus.ONGOING,
              sourceStatus: TransactionStatus.ONGOING,
              id,
            });

            // broadcast the signed tx to get hash and listen to events
            const response = await signingClient.broadcastTx(
              TxRaw.encode(tx).finish()
            );

            const hash = response.transactionHash;

            if (hash) {
              resetQueriesAfterTxSigned();
            }

            // Dispatch event so it can be listened from outside the widget
            WidgetEvents.getInstance().dispatchSwapExecuteCall(route, hash);

            const txParams: TransactionParams | undefined = setTransactionState(
              {
                route,
                txHash: hash,
                userAddress: sourceUserAddress,
                status: TransactionStatus.ONGOING,
                sourceStatus: TransactionStatus.ONGOING,
                axelarUrl: undefined,
                id,
              }
            );

            if (txParams) {
              addSwapTransaction({
                ...txParams,
                params: route.params,
                estimate: route.estimate,
              });
            }

            return response.code === 0;
          }
        } catch (error) {
          console.error("Error executing Cosmos transaction", error);
          if (isUserRejectionError(normalizeError(error))) {
            throw new Error("Request rejected");
          }
        }
      }
      throw new Error("Need all parameters");
    }
  );

  // If the transaction is replaced, we need to update the transaction hash
  // Transaction replaced can mean that the user has speed up the transaction for example
  // Could also be cancelled
  const handleTransactionReplacementError = useCallback(
    async ({
      error,
      route,
      status,
      sourceStatus,
      userAddress,
      axelarUrl,
      id,
    }: {
      error: any;
      route?: RouteResponse["route"];
      status: TransactionStatus;
      sourceStatus: TransactionStatus;
      userAddress?: string;
      axelarUrl?: string;
      id: string;
    }): Promise<TransactionReceipt> => {
      if (route && isError(error, "TRANSACTION_REPLACED")) {
        const txReplacementError = error;

        const { hash: newHash, nonce: newNonce } =
          txReplacementError.replacement;

        if (route.transactionRequest) {
          const txParams = setTransactionState({
            route,
            txHash: newHash,
            nonce: newNonce,
            userAddress: sourceUserAddress,
            status,
            sourceStatus,
            axelarUrl: undefined,
            id,
          });

          if (txParams) {
            replaceSwapTransactionNonce(newNonce, sourceUserAddress!, {
              ...txParams,
              params: route.params,
              estimate: route.estimate,
            });
          }
        }

        try {
          const response = await txReplacementError.replacement.wait();
          if (!response) {
            throw new Error("Transaction receipt is null");
          }
          return response;
        } catch (replacementError) {
          // Maybe the transaction was replaced again
          // recursive call
          return handleTransactionReplacementError({
            error,
            route,
            status,
            sourceStatus,
            userAddress,
            axelarUrl,
            id,
          });
        }
      } else {
        throw error;
      }
    },
    [replaceSwapTransactionNonce, setTransactionState, sourceUserAddress]
  );

  const swapMutationEvm = useMutation(
    async ({ id, route }: SwapMutationParams) => {
      await changeNetworkIfNeeded.mutateAsync();

      if (!route || !squid || !evmSigner) {
        throw new Error("Need all parameters");
      }

      dispatchSignatureRequestEvent(route);

      const txResponse = (await squid.executeRoute({
        bypassBalanceChecks: true,
        signer: evmSigner,
        route,
      })) as unknown as TransactionResponse;

      let hash = txResponse.hash;
      if (activeConnector?.id === "safe") {
        hash = await getGnosisTransactionHash(txResponse.hash);
      }

      if (hash) {
        resetQueriesAfterTxSigned();
      }

      // Dispatch event so it can be listened from outside the widget
      WidgetEvents.getInstance().dispatchSwapExecuteCall(route, hash);

      if (route.transactionRequest) {
        const txParams = setTransactionState({
          route,
          txHash: hash,
          nonce: txResponse.nonce,
          userAddress: sourceUserAddress,
          status: TransactionStatus.INITIAL_LOADING,
          sourceStatus: TransactionStatus.ONGOING,
          axelarUrl: undefined,
          id,
          depositTxVerificationSignature:
            txResponse.depositTxVerificationSignature,
        });

        if (txParams) {
          addSwapTransaction({
            ...txParams,
            params: route.params,
            estimate: route.estimate,
          });
        }
      }

      try {
        if (isProblematicConnector(activeConnector)) {
          await sleep(3_000);
        }

        return await waitForReceiptWithRetry(txResponse);
      } catch (error: any) {
        return handleTransactionReplacementError({
          error,
          route,
          status: TransactionStatus.INITIAL_LOADING,
          sourceStatus: TransactionStatus.ONGOING,
          userAddress: sourceUserAddress,
          axelarUrl: undefined,
          id,
        });
      }
    }
  );

  const swapMutationSolana = useMutation(
    async ({ id, route }: SwapMutationParams) => {
      try {
        if (!route?.transactionRequest) {
          throw new Error("Route is required");
        }
        if (!solanaSigner) {
          throw new Error("Solana signer is required");
        }
        if (!route.params.fromAddress || !route.params.toAddress) {
          throw new Error("From or to address is required");
        }

        const isChainflipDirectTransfer = isChainflipDepositRoute(route);

        // Means it's a transfer to a deposit address
        // Instead of a Swap/Contract call using a DEX like Jupiter
        if (isChainflipDirectTransfer) {
          // Get the deposit address from the squidRoute
          const depositData = useDepositAddressStore.getState().deposit;

          // Validate params
          if (!depositData?.depositAddress) {
            throw new Error("Deposit address is required");
          }

          const signature = await executeSolanaTransfer({
            amount: BigInt(route.params.fromAmount ?? ""),
            target: depositData.depositAddress,
            signer: solanaSigner,
            connection: solanaConnection,
            sourceToken: findToken(
              route.params.fromToken,
              route?.params?.fromChain
            ),
            onSigned: (txHash) => {
              WidgetEvents.getInstance().dispatchSwapExecuteCall(route, txHash);

              const txParams = setTransactionState({
                route,
                txHash,
                transactionIdForStatus: depositData.statusTrackingId,
                userAddress: sourceUserAddress,
                status: TransactionStatus.INITIAL_LOADING,
                sourceStatus: TransactionStatus.ONGOING,
                id,
              });

              if (txParams) {
                addSwapTransaction({
                  ...txParams,
                  params: route.params,
                  estimate: route.estimate,
                });
              }
            },
          });
          return signature;
        }

        const signature = await executeSolanaSwap({
          route,
          signer: solanaSigner,
          connection: solanaConnection,
          onSigned: (txHash) => {
            WidgetEvents.getInstance().dispatchSwapExecuteCall(route, txHash);

            const txParams = setTransactionState({
              route,
              txHash,
              transactionIdForStatus: undefined, // automatically take the tx hash because it's not a chainflip deposit
              userAddress: sourceUserAddress,
              status: TransactionStatus.INITIAL_LOADING,
              sourceStatus: TransactionStatus.ONGOING,
              id,
            });

            if (txParams) {
              addSwapTransaction({
                ...txParams,
                params: route.params,
                estimate: route.estimate,
              });
            }
          },
        });

        return signature;
      } catch (error) {
        console.error("Solana transaction failed:", error);
        throw error instanceof Error
          ? error
          : new Error("Failed to execute Solana transaction");
      }
    }
  );

  const swapMutationBitcoin = useMutation(
    async ({ id, route }: SwapMutationParams) => {
      if (!bitcoinSigner || !route?.transactionRequest) {
        throw new Error("Need all parameters");
      }

      switch (route.transactionRequest.type) {
        case SquidDataType.ChainflipDepositAddress: {
          const {
            depositAddress,
            amount: sendAmount,
            statusTrackingId,
          } = useDepositAddressStore.getState().deposit ?? {};

          const allParamsValid =
            route && bitcoinSigner && depositAddress && sendAmount;

          if (!allParamsValid) {
            throw new Error("Need all parameters");
          }

          await changeNetworkIfNeeded.mutateAsync();

          dispatchSignatureRequestEvent(route);

          const { txHash } = await bitcoinSigner.sendBTC(
            depositAddress,
            Number(sendAmount)
          );

          if (txHash) {
            resetQueriesAfterTxSigned();
          }

          // Dispatch event so it can be listened from outside the widget
          WidgetEvents.getInstance().dispatchSwapExecuteCall(route, txHash);

          if (route.transactionRequest) {
            const txParams = setTransactionState({
              route,
              txHash,
              // When bridging from Bitcoin we need to send the chainflipId to the status endpoint
              // instead of the Bitcoin transaction hash
              transactionIdForStatus: statusTrackingId,
              userAddress: sourceUserAddress,
              status: TransactionStatus.INITIAL_LOADING,
              sourceStatus: TransactionStatus.ONGOING,
              axelarUrl: undefined,
              id,
            });

            if (txParams) {
              addSwapTransaction({
                ...txParams,
                params: route.params,
                estimate: route.estimate,
              });
            }
          }

          break;
        }
        case SquidDataType.DepositAddressCalldata: {
          if (!isOnChainTxData(route.transactionRequest)) {
            throw new Error("Invalid route data");
          }

          const { data: transactionHex } = route.transactionRequest;

          await changeNetworkIfNeeded.mutateAsync();

          dispatchSignatureRequestEvent(route);

          const { txHash } = await bitcoinSigner.signPsbt(transactionHex);

          if (txHash) {
            resetQueriesAfterTxSigned();
          }

          // Dispatch event so it can be listened from outside the widget
          WidgetEvents.getInstance().dispatchSwapExecuteCall(route, txHash);

          if (route.transactionRequest) {
            const txParams = setTransactionState({
              route,
              txHash,
              userAddress: sourceUserAddress,
              status: TransactionStatus.INITIAL_LOADING,
              sourceStatus: TransactionStatus.ONGOING,
              axelarUrl: undefined,
              id,
            });

            if (txParams) {
              addSwapTransaction({
                ...txParams,
                params: route.params,
                estimate: route.estimate,
              });
            }
          }

          break;
        }
        default: {
          throw new Error(
            `Unhandled route type ${route.transactionRequest.type}`
          );
        }
      }
    }
  );

  const swapMutationXrpl = useMutation(
    async ({ id, route }: SwapMutationParams) => {
      if (!route?.transactionRequest || !xrplSigner) {
        throw new Error("Need all parameters");
      }

      if (!isOnChainTxData(route.transactionRequest)) {
        throw new Error("Invalid route type");
      }

      const { data } = route.transactionRequest;
      const paymentTx = parseXrplPaymentTx(data);

      if (!paymentTx) throw new Error("Could not parse transaction");

      const fromChainId = route.params.fromChain;
      const xrplNetwork = getXrplNetwork(fromChainId);

      if (xrplNetwork == null) {
        throw new Error(`No XRPL network found for chainId '${fromChainId}'`);
      }

      const txRes = await xrplSigner.signAndSubmit({
        tx: paymentTx,
        network: xrplNetwork,
      });

      const txParams = setTransactionState({
        txHash: txRes.hash,
        id,
        sourceStatus: TransactionStatus.ONGOING,
        status: TransactionStatus.ONGOING,
        route: route,
        userAddress: sourceUserAddress,
      });

      if (txParams && route) {
        addSwapTransaction({
          ...txParams,
          params: route.params,
          estimate: route.estimate,
        });
      }

      if (txRes.status !== XrplTxStatus.SUCCESS) {
        throw new Error(`Transaction failed with status: ${txRes.status}`);
      }
    },
    {}
  );

  const swapMutationSui = useMutation(
    async ({ id, route }: SwapMutationParams) => {
      if (!route?.transactionRequest || !suiSigner || !fromChain) {
        throw new Error("Need all parameters");
      }

      if (!isOnChainTxData(route.transactionRequest)) {
        throw new Error("Invalid route type");
      }

      const suiWalletState = connectedWalletsByChainType[ChainType.SUI];
      if (!suiWalletState.account) {
        throw new Error("Sui wallet is missing account data");
      }

      const fromChainId = route.params.fromChain;
      const suiChain = getSuiChain(fromChainId);

      if (!suiChain) {
        throw new Error(
          `Source chain (${fromChainId}) does not match any Sui chain`
        );
      }

      const suiClient = await getClient<ChainType.SUI>(fromChain);

      const txJson = route.transactionRequest.data;
      const tx = SuiTransaction.from(txJson);

      const signedTx = await suiSigner.signTransaction({
        transaction: tx,
        account: suiWalletState.account,
        chain: suiChain,
      });

      // execute transaction without waiting for confirmation
      const txResponse = await suiClient.executeTransactionBlock({
        signature: signedTx.signature,
        transactionBlock: signedTx.bytes,
      });

      const txHash = txResponse.digest;

      if (txHash) {
        resetQueriesAfterTxSigned();
      }

      WidgetEvents.getInstance().dispatchSwapExecuteCall(route, txHash);

      const txParams = setTransactionState({
        route,
        txHash,
        userAddress: sourceUserAddress,
        status: TransactionStatus.INITIAL_LOADING,
        sourceStatus: TransactionStatus.ONGOING,
        id,
      });

      if (txParams) {
        addSwapTransaction({
          ...txParams,
          params: route.params,
          estimate: route.estimate,
        });
      }

      // wait for transaction confirmation
      await suiClient.waitForTransaction({
        digest: txHash,
      });
    },
    {}
  );

  const swapMutationStellar = useMutation(
    async ({ id, route }: SwapMutationParams) => {
      if (
        !stellarSigner ||
        !route?.transactionRequest ||
        !squid ||
        !fromChain
      ) {
        throw new Error("Need all parameters");
      }

      if (!isOnChainTxData(route.transactionRequest)) {
        throw new Error("Invalid route type");
      }

      const fromChainId = route.params.fromChain;
      const stellarNetwork = getStellarNetwork(fromChainId);

      if (stellarNetwork == null) {
        throw new Error(`No Stellar network found for chainId ${fromChainId}`);
      }

      const client = await getClient<ChainType.STELLAR>(fromChain);

      // TODO: at the moment we're receiving different formats depending on the route type.
      //
      // For OnChainExecution we get a single operation xdr string, so we need to build
      // a full transaction frontend-side (by adding the operation to a new transaction).
      //
      // For DepositAddressCalldata we get the full transaction xdr,
      // so we just need to send it to the wallet for signing.
      //
      // In the future this will be standardized backend-side so we have a unified flow for all route types.
      let txXdrToSign: string;
      if (route.transactionRequest.type === SquidDataType.OnChainExecution) {
        const { data: operationXdrHex, gasPrice } = route.transactionRequest;

        const { address } = await stellarSigner.getAddress();

        const account = await client.getAccount(address);

        const operation = xdr.Operation.fromXDR(operationXdrHex, "hex");
        const builtTransaction = new TransactionBuilder(account, {
          fee: gasPrice || (BigInt(BASE_FEE) * BigInt(2)).toString(),
          networkPassphrase: stellarNetwork,
        })
          .addOperation(operation)
          .setTimeout(300)
          .build();

        // This is a smart-contract transaction, so it needs to be simulated first
        const preparedTransaction =
          await client.prepareTransaction(builtTransaction);

        txXdrToSign = preparedTransaction.toXDR();
      } else if (
        route.transactionRequest.type === SquidDataType.DepositAddressCalldata
      ) {
        // For this route type, we get the full transaction xdr in the data field.
        // This is a payment transaction, so simulation must be skipped.
        txXdrToSign = route.transactionRequest.data;
      } else {
        throw new Error(`Invalid route type ${route.transactionRequest.type}`);
      }

      const { signedTxXdr } = await stellarSigner.signTransaction(txXdrToSign, {
        networkPassphrase: stellarNetwork,
      });

      const signedTransaction = new StellarTransaction(
        signedTxXdr,
        stellarNetwork
      );

      // get hash before submitting
      const hash = signedTransaction.hash().toString("hex");

      if (hash) {
        resetQueriesAfterTxSigned();
      }

      // Dispatch event so it can be listened from outside the widget
      WidgetEvents.getInstance().dispatchSwapExecuteCall(route, hash);

      const txParams = setTransactionState({
        route,
        txHash: hash,
        userAddress: sourceUserAddress,
        status: TransactionStatus.INITIAL_LOADING,
        sourceStatus: TransactionStatus.ONGOING,
        id,
      });

      if (txParams) {
        addSwapTransaction({
          ...txParams,
          params: route.params,
          estimate: route.estimate,
        });
      }

      const sentTransaction = await client.sendTransaction(signedTransaction);

      await client.waitForTransaction(sentTransaction.hash);
    },
    {}
  );

  const swapMutationCanton = useMutation(
    async ({ id, route }: SwapMutationParams) => {
      if (!route?.transactionRequest) {
        throw new Error("Route is required");
      }
      if (!cantonSigner) {
        throw new Error("Canton signer is required");
      }
      if (!isDepositAddressDirectTransferRoute(route.transactionRequest)) {
        throw new Error("Invalid Canton route type");
      }

      const receiver = route.transactionRequest.target;
      const orderHash = route.transactionRequest.data;
      const fromAmount = route.params.fromAmount;
      const token = findToken(route.params.fromToken, route.params.fromChain);

      if (!sourceUserAddress || !fromAmount || !token) {
        throw new Error("Need all parameters");
      }

      const amount = formatBNToReadable(fromAmount, token.decimals);

      dispatchSignatureRequestEvent(route);

      const transferParams = await buildCantonTransfer(cantonSigner, {
        sender: sourceUserAddress,
        receiver,
        amount,
        token,
        memo: orderHash,
        nowMs: Date.now(),
      });

      const result = await cantonSigner.prepareExecuteAndWait(transferParams);

      const txHash = result.tx?.payload?.updateId ?? "";

      if (txHash) {
        resetQueriesAfterTxSigned();
      }

      WidgetEvents.getInstance().dispatchSwapExecuteCall(route, txHash);

      const txParams = setTransactionState({
        route,
        txHash: txHash,
        userAddress: sourceUserAddress,
        status: TransactionStatus.INITIAL_LOADING,
        sourceStatus: TransactionStatus.ONGOING,
        id,
      });

      if (txParams) {
        addSwapTransaction({
          ...txParams,
          params: route.params,
          estimate: route.estimate,
        });
      }

      return txHash;
    }
  );

  const handleTransactionSuccess = useCallback(
    (id: string) => {
      const currentTx = getTransaction(id);

      queryClient.invalidateQueries(getPrefixKey(QueryKeys.Balances));

      if (isCompletedOnSourceTx && currentTx?.transactionId) {
        replaceSwapTransactionStatus({
          transactionId: currentTx.transactionId,
          statusResponse: currentTx.statusResponse,
          status: TransactionStatus.SUCCESS,
        });
      }

      setTransactionStoreState(id, {
        ...currentTx!,
        sourceStatus: TransactionStatus.SUCCESS,
        status: isCompletedOnSourceTx
          ? TransactionStatus.SUCCESS
          : TransactionStatus.ONGOING,
      });
    },
    [
      getTransaction,
      isCompletedOnSourceTx,
      queryClient,
      replaceSwapTransactionStatus,
      setTransactionStoreState,
    ]
  );

  const handleTransactionError = useCallback(
    (error: any, id: string) => {
      const currentTx = getTransaction(id);
      const errorObject = getTransactionError(error);
      handleTransactionErrorEvents({
        error: errorObject,
        transactionParams: currentTx,
        walletProviderName: connectedSourceWallet?.wallet?.name,
        squidRoute,
      });

      setTransactionStoreState(id, {
        ...currentTx!,
        status: TransactionStatus.ERROR,
        sourceStatus: TransactionStatus.ERROR,
        error: errorObject,
      });

      if (
        currentTx?.transactionId &&
        errorObject.type === TransactionErrorType.CALL_EXCEPTION
      ) {
        replaceSwapTransactionStatus({
          transactionId: currentTx.transactionId,
          statusResponse: currentTx.statusResponse,
          status: TransactionStatus.ERROR,
        });
      }
    },
    [
      connectedSourceWallet?.wallet?.name,
      getTransaction,
      replaceSwapTransactionStatus,
      setTransactionStoreState,
      squidRoute,
    ]
  );

  const swapMutation = useMutation(
    async (mutationParams: SwapMutationParams) => {
      if (!mutationParams.route?.transactionRequest) {
        throw new Error("Route is required");
      }

      const sourceChain = findChain(mutationParams.route.params.fromChain);

      if (!sourceChain) throw new Error("Could not find source chain");

      // After getting signature (if needed), continue with the swap flow
      switch (sourceChain.chainType) {
        case ChainType.COSMOS: {
          return swapMutationCosmos.mutateAsync(mutationParams);
        }
        case ChainType.EVM: {
          return swapMutationEvm.mutateAsync(mutationParams);
        }
        case ChainType.BTC: {
          return swapMutationBitcoin.mutateAsync(mutationParams);
        }
        case ChainType.SOLANA: {
          return swapMutationSolana.mutateAsync(mutationParams);
        }
        case ChainType.SUI: {
          return swapMutationSui.mutateAsync(mutationParams);
        }
        case ChainType.XRPL: {
          return swapMutationXrpl.mutateAsync(mutationParams);
        }
        case ChainType.STELLAR: {
          return swapMutationStellar.mutateAsync(mutationParams);
        }
        case ChainType.CANTON: {
          return swapMutationCanton.mutateAsync(mutationParams);
        }
        default:
          throw new Error(
            `Swap mutation not implemented for chain type: ${sourceChain.chainType}`
          );
      }
    },
    {
      onMutate: (variables) => {
        useTransactionStore.setState({
          txLocalId: variables.id,
          currentTransaction: undefined,
        });
      },
      onError: (error, variables) =>
        handleTransactionError(error, variables.id),
      onSuccess: (_data, variables) => {
        const currentTx = getTransaction(variables.id);
        handleTransactionSuccess(variables.id);

        // Dispatch postSwap event after successful swap
        if (squidRoute && sourceUserAddress && currentTx?.transactionId) {
          dispatchPostSwapEvent(squidRoute, currentTx.transactionId);
        }
      },
    }
  );

  /**
   * Execute transaction wrapper
   * Generate a unique id for the transaction (as they don't have tx hash yet)
   */
  const executeSwap = useCallback(async () => {
    const transactionId = Date.now().toString();

    // Dispatch preSwap event before executing the swap
    if (squidRoute) {
      dispatchPreSwapEvent(squidRoute);
    }

    return swapMutation.mutate({
      id: transactionId,
      route: squidRoute,
    });
  }, [swapMutation, squidRoute, dispatchPreSwapEvent]);

  /**
   * In QR-based wallets, cleans up listeners and network requests (e.g. websockets)
   * Useful to save resources i.e when the user dismisses the QR code or aborts the connection process
   */
  const cancelSwap = useCallback(() => {
    switch (fromChain?.chainType) {
      case ChainType.XRPL:
        return xrplSigner?.cancelSignAndSubmit?.();
    }
  }, [fromChain?.chainType, xrplSigner]);

  return {
    executeSwap,
    cancelSwap,
    fromToken,
    toToken,
    toChain,
    fromChain,
    isLoading: swapMutation.isLoading,
    error: swapMutation.error,
  };
};
